/**
 * Specialized Agent Node Component
 *
 * Agent node for the demo workflow diagram showing specialized agents
 * that are orchestrated by the Lead Agent.
 *
 * @module components/home/multi-agent/nodes/specialized-agent-node
 */

import { memo } from "react"
import { Handle, Position } from "@xyflow/react"
import type { NodeProps } from "@xyflow/react"
import { cn } from "@/lib/utils"

/**
 * Data for specialized agent node.
 */
export interface SpecializedAgentNodeData {
  name: string
  initials: string
  description: string
  letter: string
  color: "primary" | "secondary"
}

/**
 * SpecializedAgentNode renders an agent card in the demo diagram.
 */
function SpecializedAgentNodeComponent({ data }: NodeProps) {
  const { name, initials, description, letter, color } =
    data as SpecializedAgentNodeData

  return (
    <div className="relative">
      {/* Connection handle on the left (incoming) */}
      <Handle
        type="target"
        position={Position.Left}
        className="!h-3 !w-3 !border-2 !border-white !bg-primary"
      />

      {/* Connection handle on the right (outgoing) */}
      <Handle
        type="source"
        position={Position.Right}
        className="!h-3 !w-3 !border-2 !border-white !bg-primary"
      />

      {/* Connection handle on the bottom (for vertical connections) */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        className="!h-3 !w-3 !border-2 !border-white !bg-primary"
      />

      {/* Connection handle on the top (for vertical connections) */}
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        className="!h-3 !w-3 !border-2 !border-white !bg-primary"
      />

      {/* Main card */}
      <div className="relative w-[200px] rounded-lg border border-neutral-600 bg-neutral-700 px-3 py-2.5 shadow-lg">
        {/* Content */}
        <div className="flex items-center gap-2.5">
          {/* Agent icon with initials */}
          <div className="relative">
            <div
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-lg text-sm font-bold text-white",
                color === "primary" ? "bg-primary" : "bg-secondary"
              )}
            >
              {initials}
            </div>

            {/* Status dot */}
            <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-neutral-700 bg-primary" />
          </div>

          {/* Text content */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h3 className="truncate text-sm font-semibold text-white">
                {name}
              </h3>
              <span className="rounded bg-neutral-600 px-1.5 py-0.5 text-xs font-bold text-neutral-300">
                {letter}
              </span>
            </div>
            <p className="mt-0.5 truncate text-xs text-neutral-300">
              {description}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export const SpecializedAgentNode = memo(SpecializedAgentNodeComponent)
